/**
 * Local LLM - LM Studio, Ollama, llama.cpp
 * VRAM bazli model onerileri + OS kurulum yonergeleri
 */
import axios from 'axios'
import { AlertCircle, Check, ChevronDown, ChevronRight, Monitor, Plus, Trash2, Wifi, WifiOff } from 'lucide-react'
import { useEffect, useState } from 'react'
import { API_URL } from '../../../config/api'

interface LocalEndpoint {
  id: number
  label: string
  endpoint: string
  type: string
  model: string | null
  is_active: boolean
  last_ping: string | null
  ping_ms: number | null
}

interface AddForm {
  label: string
  endpoint: string
  type: string
  model: string
}

// VRAM katmanlari - hangi modeller calisir
const VRAM_TIERS = [
  {
    vram: '4 GB',
    color: '#f59e0b',
    badge: 'Entry',
    models: [
      { name: 'Llama 3.2 3B (Q4)', size: '2.0 GB', speed: 'Fast', quality: 'Good' },
      { name: 'Qwen 2.5 3B (Q4)', size: '1.9 GB', speed: 'Fast', quality: 'Good' },
      { name: 'Phi-3 Mini (Q4)', size: '2.2 GB', speed: 'Fast', quality: 'Good' },
      { name: 'Gemma 2 2B (Q4)', size: '1.6 GB', speed: 'Very Fast', quality: 'Decent' },
    ]
  },
  {
    vram: '8 GB',
    color: '#3b82f6',
    badge: 'Standard',
    models: [
      { name: 'Llama 3.1 8B (Q4)', size: '4.7 GB', speed: 'Fast', quality: 'Very Good' },
      { name: 'Mistral 7B (Q4)', size: '4.1 GB', speed: 'Fast', quality: 'Very Good' },
      { name: 'Qwen 2.5 7B (Q4)', size: '4.4 GB', speed: 'Fast', quality: 'Very Good' },
      { name: 'Gemma 2 9B (Q4)', size: '5.5 GB', speed: 'Medium', quality: 'Excellent' },
    ]
  },
  {
    vram: '16 GB',
    color: '#8b5cf6',
    badge: 'Pro',
    models: [
      { name: 'Llama 3.1 13B (Q4)', size: '7.9 GB', speed: 'Medium', quality: 'Excellent' },
      { name: 'Qwen 2.5 14B (Q4)', size: '8.9 GB', speed: 'Medium', quality: 'Excellent' },
      { name: 'Mistral Nemo 12B (Q4)', size: '7.1 GB', speed: 'Medium', quality: 'Excellent' },
      { name: 'DeepSeek R1 14B (Q4)', size: '8.5 GB', speed: 'Medium', quality: 'Excellent' },
    ]
  },
  {
    vram: '32 GB',
    color: '#10b981',
    badge: 'Power',
    models: [
      { name: 'Llama 3.1 70B (Q2)', size: '26 GB', speed: 'Slow', quality: 'Near GPT-4' },
      { name: 'Qwen 2.5 32B (Q4)', size: '19 GB', speed: 'Medium', quality: 'Near GPT-4' },
      { name: 'DeepSeek R1 32B (Q4)', size: '19 GB', speed: 'Medium', quality: 'Near GPT-4' },
      { name: 'Mixtral 8x7B (Q4)', size: '26 GB', speed: 'Slow', quality: 'Excellent' },
    ]
  },
]

const TYPES = [
  { id: 'lmstudio', name: 'LM Studio', default_endpoint: 'http://localhost:1234' },
  { id: 'ollama', name: 'Ollama', default_endpoint: 'http://localhost:11434' },
  { id: 'llamacpp', name: 'llama.cpp', default_endpoint: 'http://localhost:8080' },
  { id: 'vllm', name: 'vLLM', default_endpoint: 'http://localhost:8000' },
  { id: 'openai_compat', name: 'OpenAI Compatible', default_endpoint: 'http://localhost:8000' },
]

// Kurulum yonergeleri - her tool icin 3 OS
const INSTALL_GUIDES: Record<string, { windows: string[]; macos: string[]; linux: string[] }> = {
  lmstudio: {
    windows: [
      'lmstudio.ai adresine git, Windows installer indir (.exe)',
      'Kurulumu calistir, LM Studio ac',
      'Sol menuden "Discover" → model ara (orn: llama-3.2-3b)',
      'Modeli indir (VRAM\'ina gore sec)',
      '"Local Server" sekmesine gec → "Start Server" tikla',
      'Endpoint: http://localhost:1234 (varsayilan)',
    ],
    macos: [
      'lmstudio.ai adresine git, macOS .dmg indir',
      'LM Studio.app\'i Applications klasorune surukle',
      'Ac → "Discover" → model ara ve indir',
      '"Local Server" → "Start Server"',
      'Endpoint: http://localhost:1234',
      'Not: Apple Silicon (M1/M2/M3) Metal GPU destekler',
    ],
    linux: [
      'lmstudio.ai → Linux AppImage indir',
      'chmod +x LM_Studio-*.AppImage',
      './LM_Studio-*.AppImage',
      'Discover → model indir',
      'Local Server → Start Server',
      'Endpoint: http://localhost:1234',
    ],
  },
  ollama: {
    windows: [
      'ollama.com/download → Windows installer indir',
      'Kurulumu calistir (otomatik servis olarak baslar)',
      'CMD/PowerShell ac: ollama pull llama3.2:3b',
      'Model inince otomatik servis baslar',
      'Endpoint: http://localhost:11434',
      'Diger modeller: ollama pull mistral, ollama pull qwen2.5',
    ],
    macos: [
      'ollama.com/download → macOS indir',
      'Ollama.app\'i Applications\'a surukle, ac',
      'Terminal: ollama pull llama3.2:3b',
      'Endpoint: http://localhost:11434',
      'Servis otomatik baslar, menu bar\'da gorunur',
      'ollama list → yuklu modelleri goster',
    ],
    linux: [
      'curl -fsSL https://ollama.com/install.sh | sh',
      'ollama pull llama3.2:3b',
      'Servis otomatik baslar: systemctl status ollama',
      'Endpoint: http://localhost:11434',
      'GPU icin: NVIDIA driver + CUDA gerekli',
      'AMD GPU: ROCm destekli sistemlerde otomatik',
    ],
  },
  llamacpp: {
    windows: [
      'github.com/ggerganov/llama.cpp → Releases → Windows binary indir',
      'GGUF model indir (HuggingFace\'den)',
      'CMD: llama-server.exe -m model.gguf --port 8080',
      'Endpoint: http://localhost:8080',
      'GPU icin: CUDA build kullan (llama-server-cuda.exe)',
      'Parametre: -ngl 99 (tum katmanlari GPU\'ya yukle)',
    ],
    macos: [
      'brew install llama.cpp',
      'GGUF model indir',
      'llama-server -m model.gguf --port 8080',
      'Endpoint: http://localhost:8080',
      'Metal GPU otomatik kullanilir (Apple Silicon)',
      'Parametre: -ngl 1 (Metal icin)',
    ],
    linux: [
      'git clone https://github.com/ggerganov/llama.cpp && cd llama.cpp',
      'make LLAMA_CUDA=1 (NVIDIA) veya make (CPU)',
      'GGUF model indir',
      './llama-server -m model.gguf --port 8080 -ngl 99',
      'Endpoint: http://localhost:8080',
      'CUDA icin: nvcc + CUDA toolkit gerekli',
    ],
  },
  vllm: {
    windows: [
      'vLLM Windows\'ta resmi desteklenmez',
      'WSL2 kullan: wsl --install',
      'WSL2 icinde Linux adimlarini takip et',
      'NVIDIA GPU + CUDA gerekli',
      'Endpoint: http://localhost:8000 (WSL2 IP ile)',
      'Alternatif: Docker Desktop + GPU passthrough',
    ],
    macos: [
      'vLLM macOS\'ta resmi desteklenmez',
      'Apple Silicon icin llama.cpp veya LM Studio kullan',
      'Docker ile: docker run vllm/vllm-openai (CPU only)',
      'Endpoint: http://localhost:8000',
      'Not: Metal GPU vLLM\'de desteklenmez',
      'Onerim: LM Studio veya Ollama kullan',
    ],
    linux: [
      'pip install vllm',
      'python -m vllm.entrypoints.openai.api_server --model meta-llama/Llama-3.1-8B-Instruct',
      'Endpoint: http://localhost:8000',
      'NVIDIA GPU + CUDA 12.1+ gerekli',
      'HuggingFace token gerekebilir: huggingface-cli login',
      'Cok GPU: --tensor-parallel-size 2',
    ],
  },
  openai_compat: {
    windows: ['Herhangi bir OpenAI uyumlu server kullan', 'Endpoint URL\'ini gir', 'API key gerekiyorsa Authorization header ekle'],
    macos: ['Herhangi bir OpenAI uyumlu server kullan', 'Endpoint URL\'ini gir', 'API key gerekiyorsa Authorization header ekle'],
    linux: ['Herhangi bir OpenAI uyumlu server kullan', 'Endpoint URL\'ini gir', 'API key gerekiyorsa Authorization header ekle'],
  },
}

export default function LocalLLMPage() {
  const [endpoints, setEndpoints] = useState<LocalEndpoint[]>([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [pinging, setPinging] = useState<Record<number, boolean>>({})
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<AddForm>({ label: '', endpoint: 'http://localhost:1234', type: 'lmstudio', model: '' })
  const [openGuide, setOpenGuide] = useState<string | null>(null)
  const [guideOS, setGuideOS] = useState<'windows' | 'macos' | 'linux'>('windows')
  const [openVram, setOpenVram] = useState<string | null>('8 GB')

  const fetchEndpoints = async () => {
    try {
      const token = localStorage.getItem('token')
      const resp = await axios.get(`${API_URL}/api/aicloud/local-llm`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setEndpoints((resp.data as { endpoints: LocalEndpoint[] }).endpoints ?? [])
    } catch { setEndpoints([]) } finally { setLoading(false) }
  }

  useEffect(() => { void fetchEndpoints() }, [])

  const handleAdd = async () => {
    if (!form.label || !form.endpoint) { setError('Label and endpoint required'); return }
    setSaving(true); setError(null)
    try {
      const token = localStorage.getItem('token')
      await axios.post(`${API_URL}/api/aicloud/local-llm`, form, { headers: { Authorization: `Bearer ${token}` } })
      setSuccess('Endpoint added')
      setShowAdd(false)
      setForm({ label: '', endpoint: 'http://localhost:1234', type: 'lmstudio', model: '' })
      void fetchEndpoints()
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } } }
      setError(err.response?.data?.detail ?? 'Failed')
    } finally { setSaving(false) }
  }

  const handlePing = async (id: number) => {
    setPinging(prev => ({ ...prev, [id]: true }))
    try {
      const token = localStorage.getItem('token')
      await axios.post(`${API_URL}/api/aicloud/local-llm/${id}/ping`, {}, { headers: { Authorization: `Bearer ${token}` } })
      void fetchEndpoints()
    } catch { setError('Ping failed') } finally { setPinging(prev => ({ ...prev, [id]: false })) }
  }

  const handleDelete = async (id: number) => {
    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${API_URL}/api/aicloud/local-llm/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      setEndpoints(prev => prev.filter(e => e.id !== id))
    } catch { setError('Failed to delete') }
  }

  const selectedType = TYPES.find(t => t.id === form.type)

  return (
    <div>
      {/* Baslik */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '4px' }}>Local LLM</h2>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
            Run models on your own GPU. Zero cost, full privacy. LM Studio, Ollama, llama.cpp, vLLM.
          </p>
        </div>
        <button onClick={() => setShowAdd(!showAdd)}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
          <Plus size={14} /> Add Endpoint
        </button>
      </div>

      {error && <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', color: '#ef4444', fontSize: '13px', marginBottom: '16px' }}><AlertCircle size={14} /> {error}</div>}
      {success && <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '8px', color: '#10b981', fontSize: '13px', marginBottom: '16px' }}><Check size={14} /> {success}</div>}

      {/* Endpoint ekleme formu */}
      {showAdd && (
        <div style={{ padding: '20px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px' }}>Add Local Endpoint</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Type</label>
              <select value={form.type}
                onChange={e => {
                  const t = TYPES.find(x => x.id === e.target.value)
                  setForm(f => ({ ...f, type: e.target.value, endpoint: t?.default_endpoint ?? f.endpoint }))
                }}
                style={{ width: '100%', padding: '8px 10px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '13px' }}>
                {TYPES.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Label</label>
              <input type="text" placeholder="e.g. My LM Studio" value={form.label}
                onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '13px' }} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px', marginBottom: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Endpoint URL</label>
              <input type="text" value={form.endpoint}
                onChange={e => setForm(f => ({ ...f, endpoint: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '13px' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Model (optional)</label>
              <input type="text" placeholder="e.g. llama3.2:3b" value={form.model}
                onChange={e => setForm(f => ({ ...f, model: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '13px' }} />
            </div>
          </div>

          {/* Secilen tool icin kurulum rehberi */}
          {form.type !== 'openai_compat' && (
            <div style={{ marginBottom: '16px', padding: '14px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>
                  {selectedType?.name} kurulum rehberi
                </span>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {(['windows', 'macos', 'linux'] as const).map(os => (
                    <button key={os} onClick={() => setGuideOS(os)}
                      style={{ padding: '3px 10px', background: guideOS === os ? '#3b82f6' : 'transparent', color: guideOS === os ? 'white' : 'var(--text-secondary)', border: `1px solid ${guideOS === os ? '#3b82f6' : 'var(--border-color)'}`, borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: 600 }}>
                      {os === 'windows' ? 'Windows' : os === 'macos' ? 'macOS' : 'Linux'}
                    </button>
                  ))}
                </div>
              </div>
              <ol style={{ margin: 0, paddingLeft: '16px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {(INSTALL_GUIDES[form.type]?.[guideOS] ?? []).map((step, i) => (
                  <li key={i} style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{step}</li>
                ))}
              </ol>
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => { void handleAdd() }} disabled={saving}
              style={{ padding: '8px 16px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={() => setShowAdd(false)}
              style={{ padding: '8px 16px', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-color)', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Mevcut endpoint'ler */}
      {loading ? (
        <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading...</div>
      ) : endpoints.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '24px' }}>
          {endpoints.map(ep => (
            <div key={ep.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', background: 'var(--card-bg)', border: `1px solid ${ep.is_active ? '#10b981' : 'var(--border-color)'}`, borderRadius: '8px' }}>
              <Monitor size={16} color={ep.is_active ? '#10b981' : '#6b7280'} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '13px', fontWeight: 600 }}>{ep.label}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>
                  {ep.endpoint}{ep.model ? ` · ${ep.model}` : ''}
                </div>
              </div>
              {ep.ping_ms !== null && <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{ep.ping_ms}ms</span>}
              <button onClick={() => { void handlePing(ep.id) }} disabled={pinging[ep.id]}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: ep.is_active ? '#10b981' : '#6b7280', padding: '4px' }} title="Ping">
                {ep.is_active ? <Wifi size={14} /> : <WifiOff size={14} />}
              </button>
              <button onClick={() => { void handleDelete(ep.id) }}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '4px' }}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* VRAM bazli model onerileri */}
      <div style={{ marginTop: endpoints.length > 0 ? '8px' : '0' }}>
        <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: 'var(--text-secondary)' }}>
          Model Recommendations by VRAM
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {VRAM_TIERS.map(tier => (
            <div key={tier.vram} style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', overflow: 'hidden' }}>
              {/* Tier baslik */}
              <button
                onClick={() => setOpenVram(openVram === tier.vram ? null : tier.vram)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                <div style={{ width: '52px', height: '24px', borderRadius: '6px', background: `${tier.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: tier.color }}>{tier.badge}</span>
                </div>
                <span style={{ fontSize: '14px', fontWeight: 700, color: tier.color }}>{tier.vram} VRAM</span>
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{tier.models.length} models available</span>
                <div style={{ marginLeft: 'auto', color: 'var(--text-secondary)' }}>
                  {openVram === tier.vram ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </div>
              </button>

              {/* Model listesi */}
              {openVram === tier.vram && (
                <div style={{ borderTop: '1px solid var(--border-color)', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {tier.models.map(model => (
                    <div key={model.name} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', background: 'var(--body-bg)', borderRadius: '8px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '13px', fontWeight: 600 }}>{model.name}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Size: {model.size}</div>
                      </div>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <span style={{ fontSize: '11px', padding: '2px 8px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '4px', color: 'var(--text-secondary)' }}>
                          {model.speed}
                        </span>
                        <span style={{ fontSize: '11px', padding: '2px 8px', background: `${tier.color}15`, border: `1px solid ${tier.color}40`, borderRadius: '4px', color: tier.color }}>
                          {model.quality}
                        </span>
                      </div>
                    </div>
                  ))}

                  {/* Kurulum rehberi linki */}
                  <div style={{ marginTop: '8px', padding: '10px 12px', background: `${tier.color}08`, border: `1px solid ${tier.color}25`, borderRadius: '8px' }}>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                      Recommended setup for {tier.vram}:
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {TYPES.slice(0, 3).map(t => (
                        <button key={t.id}
                          onClick={() => {
                            setShowAdd(true)
                            setForm(f => ({ ...f, type: t.id, endpoint: t.default_endpoint }))
                            setOpenGuide(t.id)
                          }}
                          style={{ fontSize: '11px', padding: '3px 10px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '4px', cursor: 'pointer', color: 'var(--text-primary)' }}>
                          {t.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Genel kurulum rehberleri - collapsed */}
      <div style={{ marginTop: '20px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: 'var(--text-secondary)' }}>
          Installation Guides
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {TYPES.filter(t => t.id !== 'openai_compat').map(tool => (
            <div key={tool.id} style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', overflow: 'hidden' }}>
              <button
                onClick={() => setOpenGuide(openGuide === tool.id ? null : tool.id)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                <Monitor size={14} color="#3b82f6" />
                <span style={{ fontSize: '13px', fontWeight: 600 }}>{tool.name}</span>
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{tool.default_endpoint}</span>
                <div style={{ marginLeft: 'auto', color: 'var(--text-secondary)' }}>
                  {openGuide === tool.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </div>
              </button>
              {openGuide === tool.id && (
                <div style={{ borderTop: '1px solid var(--border-color)', padding: '16px' }}>
                  <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
                    {(['windows', 'macos', 'linux'] as const).map(os => (
                      <button key={os} onClick={() => setGuideOS(os)}
                        style={{ padding: '4px 12px', background: guideOS === os ? '#3b82f6' : 'transparent', color: guideOS === os ? 'white' : 'var(--text-secondary)', border: `1px solid ${guideOS === os ? '#3b82f6' : 'var(--border-color)'}`, borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>
                        {os === 'windows' ? '🪟 Windows' : os === 'macos' ? '🍎 macOS' : '🐧 Linux'}
                      </button>
                    ))}
                  </div>
                  <ol style={{ margin: 0, paddingLeft: '18px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {(INSTALL_GUIDES[tool.id]?.[guideOS] ?? []).map((step, i) => (
                      <li key={i} style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>{step}</li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
